import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch } from 'react-redux';
import { apiClient } from '../../../utils/axios';
import { isString } from 'lodash';
import { useState } from 'react';
import { AxiosError } from 'axios';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';

const createPurchaseCreditNoteRegister = async ({ payload }) => {
  const profileId = payload?.accounting_customer_party?.profile_id;
  if (!profileId) {
    throw new Error('CUSTOMER_PROFILE_REQUIRED');
  }
  const response = await apiClient.post(`/document-registers/purchase-credit-notes/${profileId}`, payload);
  return response.data;
};

export const useCreatePurchaseCreditNoteRegisterHandler = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { purchaseInvoiceRegister } = useTranslatedMessages();
  const messages = purchaseInvoiceRegister?.handlers || {};
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: createPurchaseCreditNoteRegister,
    onSuccess: (data, variables) => {
      dispatch(
        showSnackbar({
          message: messages.create_credit_note_success ?? 'Credit note register entry created successfully.',
        }),
      );
      queryClient.invalidateQueries({ queryKey: ['purchase-credit-note-register'] });
      onSuccess && onSuccess(data, variables);
      setError(null);
    },
    onError: (err) => {
      if (err instanceof AxiosError) {
        if (err.response?.status === 400) {
          setError(err.response.data?.errors);
          return;
        }
        dispatch(
          showSnackbar({
            message: err.response?.data?.message ?? err.message ?? 'An error occurred.',
            severity: 'error',
          }),
        );
      } else {
        const message =
          err.message === 'CUSTOMER_PROFILE_REQUIRED'
            ? (messages.customer_profile_required ?? 'Customer profile is required')
            : isString(err.message)
              ? err.message
              : JSON.stringify(err.message);
        dispatch(showSnackbar({ message, severity: 'error' }));
      }
    },
  });

  const onSubmit = async (payload) => {
    const profileId = payload?.accounting_customer_party?.profile_id;
    if (!profileId) {
      dispatch(
        showSnackbar({
          message: messages.customer_profile_required ?? 'Customer profile is required',
          severity: 'error',
        }),
      );
      return;
    }
    mutation.mutate({ payload });
  };

  return {
    data: mutation.data,
    isError: mutation.isError,
    error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    onSubmit,
  };
};
